import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Icon from "../components/Icon";
import { useData } from "../data/DataProvider";
import { placeCandidate } from "../services/compatibility";
import "../styles.css";

const ROLE_OPTIONS = ["Sr. Supt", "Supt", "Finish Supt", "Asst. Supt", "FE", "Sr. PM", "PM", "APM", "PX"].sort((a, b) => a.localeCompare(b));
const TRAIT_FULL = { A: "Autonomy", B: "Social", C: "Pace", D: "Conformity" };
const TRAIT_HINT = {
  A: "Low = supportive · High = driver",
  B: "Low = analytical · High = communicator",
  C: "Low = fast/urgent · High = steady/methodical",
  D: "Low = flexible · High = detail/quality",
};

// Suggest an archetype label from the slider values (rough heuristic)
function inferArchetype(t) {
  const { A, B, C, D } = t;
  if (A >= 6 && B >= 6) return "Rainmaker";
  if (A >= 7 && D <= 5) return "Trailblazer";
  if (A >= 6 && C <= 4) return "Enterpriser";
  if (B >= 7) return "Persuader";
  if (C >= 6 && D >= 6 && A <= 5) return "Craftsman";
  if (D >= 7 && C >= 6) return "Specialist";
  if (D >= 6 && A >= 6) return "Architect";
  if (C >= 6 && D >= 6) return "Guardian";
  return "Generalist";
}

function Slider({ trait, value, onChange }) {
  return (
    <div className="ph-slider-row">
      <div className="ph-slider-label">
        <span className="ph-slider-letter">{trait}</span>
        <div>
          <div className="ph-slider-name">{TRAIT_FULL[trait]}</div>
          <div className="ph-slider-hint">{TRAIT_HINT[trait]}</div>
        </div>
      </div>
      <input
        type="range" min="0" max="10" value={value}
        onChange={e => onChange(trait, parseInt(e.target.value))}
        className="ph-range"
      />
      <div className="ph-slider-val">{value}</div>
    </div>
  );
}

export default function Placement() {
  const { people: peopleRaw, projects, teamMembers, loading, error } = useData();
  const allPeople = (peopleRaw || []).filter(p => p.IsActive !== false && p.Archetype);
  const activeProjects = (projects || []).filter(p => p.Status === "Active");

  const [role, setRole] = useState("PM");
  const [traits, setTraits] = useState({ A: 7, B: 5, C: 4, D: 5 });

  const setTrait = (t, v) => setTraits(prev => ({ ...prev, [t]: v }));

  // Build the phantom candidate object
  const candidate = useMemo(() => ({
    FullName: "Hypothetical Candidate",
    CompanyRole: role,
    Archetype: inferArchetype(traits),
    Survey_A: traits.A, Survey_B: traits.B, Survey_C: traits.C, Survey_D: traits.D,
    Survey_EU: 50, Survey_L: 6, Survey_I: 6,
  }), [role, traits]);

  const placements = useMemo(
    () => placeCandidate(candidate, activeProjects, teamMembers || [], allPeople),
    [candidate, activeProjects, teamMembers, allPeople]
  );

  const archetype = inferArchetype(traits);

  if (loading) return <div className="page"><div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading teams…</div></div></div>;
  if (error) return <div className="page"><div className="empty"><div className="empty-text">Couldn't load data: {error}</div></div></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Candidate Placement</div>
          <div className="page-subtitle">Define a hypothetical person — see which teams they'd fit best</div>
        </div>
      </div>

      <div className="ph-grid">
        {/* Phantom person builder */}
        <div className="card">
          <div className="card-header"><div className="card-title">Build the Candidate</div></div>

          <div className="ph-avatar">
            <Icon name="user-search" size={26} />
          </div>
          <div className="ph-archetype-readout">
            Profile reads as: <strong>{archetype}</strong>
          </div>

          <div className="form-group" style={{ marginTop: 16 }}>
            <label className="form-label">Role they'd fill</label>
            <select className="form-select" value={role} onChange={e => setRole(e.target.value)}>
              {ROLE_OPTIONS.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>

          <div className="divider" />
          <div className="card-title" style={{ marginBottom: 14 }}>Behavioral Traits</div>
          {["A", "B", "C", "D"].map(t => (
            <Slider key={t} trait={t} value={traits[t]} onChange={setTrait} />
          ))}

          <div className="ph-note">
            <Icon name="bulb" size={14} /> Drag the sliders to match a real candidate's Culture Index scores — or explore what profile your teams need most.
          </div>
        </div>

        {/* Placement results */}
        <div>
          <div className="section-title mb-16">Best-Fit Teams for This Candidate</div>
          {placements.length === 0 ? (
            <div className="empty">
              <div className="empty-icon"><Icon name="user-search" size={34} /></div>
              <div className="empty-text">No profiled active teams to rank against yet.</div>
            </div>
          ) : (
            placements.map((p, i) => {
              const isBest = i === 0 && p.delta > 0;
              const col = p.delta > 0 ? "var(--green)" : p.delta < 0 ? "var(--red)" : "var(--text-muted)";
              return (
                <Link key={p.project.Title} to={`/projects/${encodeURIComponent(p.project.Title)}`} className={`ph-result ${isBest ? "best" : ""}`}>
                  {isBest && <span className="ph-best-tag">Best Fit</span>}
                  <div className="ph-result-rank">{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="ph-result-name">{p.project.JobName}</div>
                    <div className="ph-result-meta">
                      {p.project.Title} · {p.teamSize} on team · PX {p.project.ProjectExecutive}
                    </div>
                    {p.fills.length > 0 && (
                      <div className="ph-fills">
                        Fills a gap:
                        {p.fills.map(f => <span key={f} className="ph-fill-pill">{f}</span>)}
                      </div>
                    )}
                  </div>
                  <div className="ph-result-delta">
                    <div className="ph-delta-num" style={{ color: col }}>
                      {p.delta > 0 ? "+" : ""}{p.delta}
                    </div>
                    <div className="ph-delta-scores">{p.beforeScore} → {p.afterScore}</div>
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
